/* eslint-disable max-lines */
import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NgOptionComponent, NgSelectComponent } from '@ng-select/ng-select';
import dayjs from 'dayjs';
import { groupBy, omit } from 'lodash-es';
import { BehaviorSubject, combineLatest, EMPTY, firstValueFrom } from 'rxjs';
import { map, shareReplay, startWith, switchMap } from 'rxjs/operators';

import { ExtendedBudgetData } from '@models/budget/budget.model';
import { BudgetQuery } from '@models/budget/budget.query';

import { ButtonComponent } from '@shared/components/button/button.component';
import { MessagesConstants } from '@shared/constants/messages.constants';
import { TooltipDirective } from '@shared/directives/tooltip.directive';
import { MoneyPipe } from '@shared/pipes/money.pipe';
import {
  ActivityType,
  AdjustmentType,
  AmountType,
  Currency,
  DiscountSource,
  ExpenseSourceType,
  ExpenseType,
  GqlService,
  WorkflowStep,
} from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';
import { QuarterCloseChecklistPeriodCloseService } from '@shared/services/quarter-close-checklist-period-close.service';
import { OrganizationQuery } from '@shared/store/organization/organization.query';
import { WorkflowQuery } from '@shared/store/workflow/workflow.query';
import { evenRounding } from '@shared/utils';
import { Utils } from '@shared/utils/utils';

import { PeriodCloseComponent } from '../../../../period-close.component';
import { ChecklistComponentData } from '../../models/quarter-close-checklist.model';
import { QuarterCloseChecklistComponent } from '../../quarter-close-checklist.component';
import { QuarterCloseChecklistService } from '../../services/quarter-close-checklist.service';
import { ChecklistRowInfoComponent } from '../checklist-row-info/checklist-row-info.component';

export interface DiscountExpenseDetail {
  amount?: number;
  calculated_percentage?: number;
  discount_type?:
    | 'contracted_calculated_discount'
    | 'custom_calculated_discount'
    | 'custom_discount'
    | 'invoice_discount'
    | 'none'
    | 'vendor_estimate_discount';
  which_vendor?: string;
}
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgSelectComponent,
    FormsModule,
    ReactiveFormsModule,
    NgOptionComponent,
    TooltipDirective,
    ButtonComponent,
    AsyncPipe,
    MoneyPipe,
  ],
  selector: 'aux-checklist-section-discount',
  styles: [],
  templateUrl: './checklist-section-discount.component.html',
})
export class ChecklistSectionDiscountComponent implements ChecklistComponentData {
  private budgetQuery = inject(BudgetQuery);
  private checklistService = inject(QuarterCloseChecklistService);
  private gqlService = inject(GqlService);
  private organizationQuery = inject(OrganizationQuery);
  private overlayService = inject(OverlayService);
  private periodCloseComponent = inject(PeriodCloseComponent);
  private periodCloseService = inject(QuarterCloseChecklistPeriodCloseService);
  private qcChecklistComponent = inject(QuarterCloseChecklistComponent);
  private workflowQuery = inject(WorkflowQuery);

  isDiscountWorkflowLocked = this.workflowQuery.getLockStatusByWorkflowStepType(
    WorkflowStep.WF_STEP_MONTH_CLOSE_LOCK_DISCOUNTS
  );

  discountLockTooltip = computed(() =>
    this.isDiscountWorkflowLocked() ? MessagesConstants.DISCOUNT_CLOSED : ''
  );

  activity_id = '';
  calculatedAmount = 0;
  calculatedAmountType: {
    perc: string;
    type: '' | 'contracted' | 'custom';
  } = {
    perc: '',
    type: '',
  };
  contractedPercentage = 0;
  contractedPercentageStr = Utils.zeroHyphen;
  customAmount = '';
  customPercentage = '';
  data: unknown;
  discountCalc: '' | 'contracted' | 'custom' = '';
  expense_detail: DiscountExpenseDetail = {};
  vendors$ = new BehaviorSubject<
    {
      currency: Currency;
      discount_value: null | number;
      name: string;
      services_cost: number;
      vendor_id: string;
    }[]
  >([]);
  gridData$ = this.periodCloseComponent.gridData$.pipe(
    switchMap((data) => {
      return this.qcChecklistComponent.selectedQuarterMonthFormControl.valueChanges.pipe(
        startWith(this.qcChecklistComponent.selectedQuarterMonthFormControl.value),
        map(() => {
          return {
            data,
            month: this.qcChecklistComponent.selectedQuarterMonthFormControl.value,
          };
        })
      );
    }),
    map(({ data, month }) => {
      const groupedData = groupBy(data as unknown as ExtendedBudgetData[], 'vendor_id');
      const arr: {
        currency: Currency;
        discount_value: null | number;
        name: string;
        services_cost: number;
        vendor_id: string;
      }[] = [];

      for (const [vendor_id, categories] of Object.entries(groupedData)) {
        const discountRow = categories.find((c) => c.cost_category === 'Discount');
        const servicesRow = categories.find((c) => c.cost_category === 'Services');

        if (discountRow) {
          const current_month = dayjs(month).format('MMM-YYYY').toUpperCase();
          const organization = this.organizationQuery.getEntity(vendor_id);

          arr.push({
            currency: organization?.currency || Currency.USD,
            discount_value: discountRow?.accrual_override?.[current_month] ?? null,
            name: organization?.name || '',
            services_cost: servicesRow?.accrual_adjusted_obj[current_month] || 0,
            vendor_id,
          });
        }
      }

      this.vendors$.next(arr);

      return groupedData;
    }),
    shareReplay(1)
  );
  id = 'CloseDiscounts';
  invoiceDiscount: null | number = null;
  isCalculatedSelectionDisabled = true;
  loading$ = new BehaviorSubject(false);
  parent!: ChecklistRowInfoComponent;
  saveButtonDisabled$ = new BehaviorSubject(true);
  saveLoading$ = new BehaviorSubject(false);
  selectedCurrency: Currency = Currency.USD;
  selectedDiscountType: '' | 'calculated' | 'custom' | 'invoice' | 'none' | 'vendor' = '';
  selectedVendor = new FormControl();
  serviceCost = 0;
  vendorEstDiscount: null | number = null;

  clearIfNecessary(radioButtonName: 'calc' | 'selection', val: string) {
    switch (radioButtonName) {
      case 'calc':
        if (this.discountCalc === val) {
          this.discountCalc = '';
        }

        break;
      case 'selection':
        if (this.selectedDiscountType === val) {
          this.selectedDiscountType = '';
        }

        break;
    }

    this.onChange();
  }

  constructor() {
    this.gridData$.pipe(takeUntilDestroyed()).subscribe();

    combineLatest([
      this.qcChecklistComponent.selectedQuarterMonthFormControl.valueChanges.pipe(
        startWith(this.qcChecklistComponent.selectedQuarterMonthFormControl.value)
      ),
      this.selectedVendor.valueChanges.pipe(startWith(this.selectedVendor.value)),
      this.periodCloseService.isCurrentQuarterSelected,
    ])
      .pipe(
        switchMap(([month]) => {
          if (month && this.periodCloseService.selectedQuarterMonth && this.selectedVendor.value) {
            this.loading$.next(true);

            return this.gqlService.listDiscountExpenses$({
              amount_types: [AmountType.AMOUNT_DISCOUNT],
              organization_ids: [this.selectedVendor.value],
              period: dayjs(month).format('MMM-YYYY').toUpperCase(),
            });
          }

          return EMPTY;
        }),
        takeUntilDestroyed()
      )
      .subscribe((resp) => {
        if (resp.data) {
          const { activity_id, invoice, manual_adjustment, vendor_estimate } = resp.data[0];

          this.vendorEstDiscount = vendor_estimate?.amount ?? null;
          this.invoiceDiscount = invoice?.amount ?? null;
          this.activity_id = activity_id;

          if (manual_adjustment) {
            const amount = manual_adjustment.amount || 0;

            try {
              this.expense_detail = {
                which_vendor: this.selectedVendor.value,
                ...JSON.parse(manual_adjustment.expense_detail || '{}'),
                amount,
              };
              this.setOldState(amount);
            } catch (e) {
              console.error(e);
            }
          }
        }

        this.loading$.next(false);
      });

    if (this.checklistService.discountSelectedVendor) {
      this.selectVendor(this.checklistService.discountSelectedVendor);
      this.checklistService.discountSelectedVendor = '';
    }
  }

  getCurrencySymbol = (currency: Currency) => {
    return Utils.getCurrenySymbol(currency);
  };

  getCurrentExpenseDetail() {
    const expense_detail: DiscountExpenseDetail = {};

    expense_detail.which_vendor = this.selectedVendor.value;
    let amount = 0;

    switch (this.selectedDiscountType) {
      case '':
        return {};
      case 'calculated':
        amount = this.calculatedAmount;

        if (this.discountCalc === 'contracted') {
          expense_detail.discount_type = 'contracted_calculated_discount';
          expense_detail.calculated_percentage = this.contractedPercentage;
        } else {
          expense_detail.discount_type = 'custom_calculated_discount';
          expense_detail.calculated_percentage = +this.customPercentage;
        }

        break;
      case 'custom':
        amount = +this.customAmount;
        expense_detail.discount_type = 'custom_discount';
        break;
      case 'invoice':
        amount = this.invoiceDiscount || 0;
        expense_detail.discount_type = 'invoice_discount';
        break;
      case 'none':
        expense_detail.discount_type = 'none';
        break;
      case 'vendor':
        amount = this.vendorEstDiscount || 0;
        expense_detail.discount_type = 'vendor_estimate_discount';
        break;
    }

    expense_detail.amount = amount;

    return {
      expense_detail,
    };
  }

  getDiscountSource(discountType: '' | 'calculated' | 'custom' | 'invoice' | 'none' | 'vendor') {
    if (discountType === 'calculated') return DiscountSource.DISCOUNT_SOURCE_CALCULATED;

    if (discountType === 'vendor') return DiscountSource.DISCOUNT_SOURCE_VENDOR_ESTIMATE;

    if (discountType === 'invoice') return DiscountSource.DISCOUNT_SOURCE_INVOICE;

    if (discountType === 'custom') return DiscountSource.DISCOUNT_SOURCE_CUSTOM;

    return DiscountSource.DISCOUNT_SOURCE_NONE;
  }

  getExpenseSource(
    discountCalc: '' | 'contracted' | 'custom',
    discountType: '' | 'calculated' | 'custom' | 'invoice' | 'none' | 'vendor'
  ) {
    if (discountType === 'vendor') {
      return ExpenseSourceType.EXPENSE_SOURCE_VENDOR_ESTIMATE;
    }

    if (discountType === 'invoice') {
      return ExpenseSourceType.EXPENSE_SOURCE_INVOICE;
    }

    if (discountCalc === 'contracted' && discountType === 'calculated') {
      return ExpenseSourceType.EXPENSE_SOURCE_FORECAST;
    }

    return ExpenseSourceType.EXPENSE_SOURCE_MANUAL_ADJUSTMENT;
  }

  logic() {
    if (!this.selectedVendor.value) {
      return true;
    }

    if (
      this.selectedDiscountType === 'custom' &&
      (!this.customAmount || Number.isNaN(+this.customAmount))
    ) {
      return true;
    }

    const bool = !this.selectedDiscountType;

    if (bool) {
      return true;
    }

    if (this.selectedDiscountType === 'calculated') {
      const { perc, type } = this.calculatedAmountType;

      if (
        this.discountCalc !== type ||
        (this.discountCalc === 'custom' && perc !== this.customPercentage)
      ) {
        return true;
      }
    }

    if (!this.expense_detail.discount_type) {
      return false;
    }

    const { expense_detail } = this.getCurrentExpenseDetail();

    switch (this.expense_detail.discount_type) {
      case 'contracted_calculated_discount':
      case 'custom_calculated_discount':
      case 'custom_discount':
        return (
          this.expense_detail.amount === expense_detail?.amount && expense_detail?.amount !== 0
        );
      case 'invoice_discount':
      case 'none':
      case 'vendor_estimate_discount':
        return (
          this.expense_detail.discount_type === expense_detail?.discount_type &&
          this.expense_detail.which_vendor === expense_detail?.which_vendor
        );
    }
  }

  onCalculate() {
    this.calculatedAmountType = {
      perc: '',
      type: '',
    };
    switch (this.discountCalc) {
      case '':
        break;
      case 'custom':
        {
          const perc = +this.customPercentage;

          if (Number.isNaN(perc)) {
            this.isCalculatedSelectionDisabled = true;
            this.calculatedAmount = 0;

            if (this.selectedDiscountType === 'calculated') {
              this.saveButtonDisabled$.next(true);
            }

            return;
          }

          this.calculatedAmount = evenRounding((perc / 100) * -this.serviceCost);
        }

        break;
      case 'contracted':
        this.calculatedAmount = evenRounding(
          ((+this.contractedPercentage || 100) / 100) * -this.serviceCost
        );
        break;
    }

    this.calculatedAmountType = {
      perc: this.customPercentage,
      type: this.discountCalc,
    };
    this.isCalculatedSelectionDisabled = false;
    this.onChange();
  }

  onChange() {
    this.saveButtonDisabled$.next(this.logic());
  }

  async onSave() {
    const vendor_id = this.selectedVendor.value;
    const vendor = this.vendors$.getValue().find((v) => v.vendor_id === vendor_id);

    if (!vendor) {
      return;
    }

    const current_month = this.periodCloseService.selectedQuarterMonth;
    const budget_info = this.budgetQuery
      .getValue()
      .budget_info.find((i) => i.vendor_id === vendor_id);

    const { expense_detail } = this.getCurrentExpenseDetail();

    if (!expense_detail) {
      return;
    }

    this.saveLoading$.next(true);

    const resp = await firstValueFrom(
      this.gqlService.createBudgetExpense$({
        activity_id: this.activity_id,
        adjustment_type: AdjustmentType.ADJUSTMENT_AMOUNT,
        amount: expense_detail.amount ?? 0,
        amount_curr: `CURRENCY_${vendor.currency || 'USD'}`,
        amount_curr_symbol: Utils.getCurrenySymbol(vendor.currency || Currency.USD),
        amount_type: AmountType.AMOUNT_DISCOUNT,
        budget_version_id: budget_info?.budget_version_id,
        discount_source: this.getDiscountSource(this.selectedDiscountType),
        expense_detail: JSON.stringify(omit(expense_detail, 'amount')),
        expense_source: this.getExpenseSource(this.discountCalc, this.selectedDiscountType),
        expense_type_id: ExpenseType.EXPENSE_ACCRUAL_OVERRIDE,
        period_end: current_month,
        period_start: current_month,
        source: 'BASE',
      })
    );

    if (resp.success && resp.data) {
      await firstValueFrom(
        this.gqlService.updateAccruals$({
          accrual_inputs: [
            {
              activity_ids: [],
              activity_types: [ActivityType.ACTIVITY_DISCOUNT],
              organization_id: vendor.vendor_id,
            },
          ],
          period: current_month,
        })
      );
      this.checklistService.discountSelectedVendor = vendor_id;
      await firstValueFrom(this.gqlService.invalidateBudgetCache$(vendor_id));
      this.periodCloseComponent.refresh$.next(null);
      this.overlayService.success();
      this.expense_detail = expense_detail;
      this.onChange();
      this.vendors$.next(
        this.vendors$.getValue().map((v) => {
          if (v.vendor_id === vendor_id) {
            return {
              ...v,
              discount_value: expense_detail.amount || 0,
            };
          }

          return v;
        })
      );
    } else {
      this.overlayService.error(resp.errors);
    }

    this.saveLoading$.next(false);
  }

  onVendorSelected() {
    this.reset();
    this.onChange();
    const vendor_id = this.selectedVendor.value;
    const vendor = this.vendors$.getValue().find((v) => v.vendor_id === vendor_id);

    if (vendor) {
      this.serviceCost = vendor.services_cost;
    }

    const info = this.budgetQuery.getValue().budget_info.find((i) => i.vendor_id === vendor_id);

    this.contractedPercentage = info?.expense_amounts?.[0]?.amount_perct || 0;
    this.contractedPercentageStr = Utils.percentageFormatter(this.contractedPercentage / 100, {
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
    });
    this.selectedCurrency = vendor?.currency || Currency.USD;
  }

  reset() {
    this.serviceCost = 0;
    this.customAmount = '';
    this.selectedDiscountType = '';
    this.customPercentage = '';
    this.discountCalc = '';
    this.vendorEstDiscount = null;
    this.invoiceDiscount = null;
    this.calculatedAmount = 0;
    this.contractedPercentage = 0;
    this.contractedPercentageStr = Utils.zeroHyphen;
    this.activity_id = '';
    this.isCalculatedSelectionDisabled = true;
    this.calculatedAmountType = {
      perc: '',
      type: '',
    };
  }

  selectVendor(vendor_id: string) {
    this.selectedVendor.setValue(vendor_id);
    this.onVendorSelected();
  }

  setOldState(amount: number) {
    switch (this.expense_detail.discount_type) {
      case 'contracted_calculated_discount':
        this.discountCalc = 'contracted';
        this.selectedDiscountType = 'calculated';
        this.calculatedAmount = amount;
        break;
      case 'custom_calculated_discount':
        this.discountCalc = 'custom';
        this.customPercentage = (this.expense_detail.calculated_percentage ?? '') + '';
        this.selectedDiscountType = 'calculated';
        this.calculatedAmount = amount;
        break;
      case 'custom_discount':
        this.selectedDiscountType = 'custom';
        this.customAmount = amount + '';
        break;
      case 'invoice_discount':
        this.selectedDiscountType = 'invoice';
        break;
      case 'none':
        this.selectedDiscountType = 'none';
        break;
      case 'vendor_estimate_discount':
        this.selectedDiscountType = 'vendor';
        break;
      default:
        break;
    }
  }
}
